import React, { useState, useEffect, useContext } from 'react';
import CartContext from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { productAPI } from '../utils/api';
import { Truck, MessageCircle, PackageSearch, BadgeCheck } from 'lucide-react';

/* Store WhatsApp, same number the backend sends payment instructions from. */
const WHATSAPP_NUMBER = '601151214897';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;

/* ── What we actually promise ──────────────────────────────────
   Deliberately not headline numbers. We are new, so every claim
   here is one a customer can hold us to on their first order. */
const PROMISES = [
    { title: 'Free delivery', sub: 'Every order, nationwide. No minimum spend.' },
    { title: 'Pay after ordering', sub: 'Bank transfer or DuitNow QR — nothing upfront.' },
    { title: 'Tracked shipping', sub: 'You get the courier and tracking number.' },
    { title: 'A real person', sub: 'Questions answered on WhatsApp, not a bot.' },
];

/* ── Category cards ────────────────────────────────────────── */
const CATEGORIES = [
    {
        title: 'Taping Guides',
        desc: 'Free step-by-step guides for common strappings — ankle, wrist, shoulder and more.',
        href: '/training-guides',
        label: 'Read the guides',
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
        ),
    },
    {
        title: 'Tapes & Bandages',
        desc: 'Rigid tape, kinesiology tape, EAB, cohesive bandage and underwrap — in the sizes you actually use.',
        href: '/catalog',
        label: 'Shop the range',
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
        ),
    },
    {
        title: 'Not sure what to order?',
        desc: 'Tell us the sport, the joint and the budget. We will point you at the right tape — no hard sell.',
        href: WHATSAPP_LINK,
        label: 'Ask on WhatsApp',
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
        ),
    },
];

/* ── How ordering works ────────────────────────────────────────
   Payment is manual over WhatsApp, which is unusual enough that
   spelling it out up front removes the main reason to hesitate. */
const STEPS = [
    { n: '1', title: 'Add to cart and check out', desc: 'Just your name, address and WhatsApp number. No account needed.' },
    { n: '2', title: 'We message you the details', desc: 'Bank account and DuitNow QR arrive on WhatsApp right after you order.' },
    { n: '3', title: 'Pay and send the receipt', desc: 'Transfer the amount, then send us the screenshot on the same chat.' },
    { n: '4', title: 'We pack and ship', desc: 'Once payment clears we post it out and send you the tracking number.' },
];

/* ── FAQ ───────────────────────────────────────────────────────
   Stands in for testimonials. We are new and have no reviews
   worth quoting, so answer the questions instead. */
const FAQS = [
    {
        q: 'Do I have to pay before you send anything?',
        a: 'Yes — we ask for payment before we pack the order, and we send you a receipt with an order number you can check any time. If something is wrong with your order, message us on WhatsApp and we will sort it out.',
    },
    {
        q: 'How much is delivery?',
        a: 'Nothing. Delivery is free on every order to anywhere in Malaysia, with no minimum spend. The price you see on the product is the price you pay.',
    },
    {
        q: 'How long does it take?',
        a: 'We pack orders once payment is confirmed, then hand them to the courier. You get the courier name and tracking number on WhatsApp so you can follow it yourself.',
    },
    {
        q: 'Can I ask a question before I buy?',
        a: 'Please do. Message the store on WhatsApp — it is the same number that handles orders, and there is a person on the other end.',
    },
];

const HomePage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addToCart } = useContext(CartContext);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await productAPI.getAll();
                setProducts(response.data.slice(0, 6)); // Featured products
            } catch (err) {
                console.error('Failed to fetch products:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const handleAddToCart = (productId, quantity = 1, variantId = null) => addToCart(productId, quantity, variantId);

    return (
        <div className="overflow-x-hidden">

            {/* ── HERO ──────────────────────────────────────────────── */}
            <section
                className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 40%, #1e1b4b 70%, #0a0a14 100%)' }}
            >
                {/* Soft radial glows */}
                <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
                <div className="absolute bottom-1/4 right-1/5 w-[400px] h-[400px] rounded-full opacity-15 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }} />

                {/* Dot grid */}
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6 pt-28 pb-24">
                    {/* Pill badge */}
                    <div className="inline-flex items-center gap-2 border border-blue-500/40 rounded-full px-5 py-2 text-blue-300 text-xs font-medium tracking-widest mb-10"
                        style={{ background: 'rgba(59,130,246,0.12)', backdropFilter: 'blur(8px)' }}>
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                        SPORTS TAPING &amp; PITCHSIDE SUPPLIES
                    </div>

                    {/* Headline — big, bold, no logo here */}
                    <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold leading-none tracking-tight mb-6">
                        <span className="block text-white">Tape that</span>
                        <span className="block" style={{
                            background: 'linear-gradient(135deg, #60a5fa 0%, #818cf8 50%, #a78bfa 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}>holds.</span>
                    </h1>

                    <p className="text-lg md:text-xl text-slate-300 font-light mb-12 max-w-2xl mx-auto leading-relaxed">
                        Rigid tape, kinesiology tape, bandages and pitchside kit for physios, trainers and athletes.
                        Shipped free anywhere in Malaysia.
                    </p>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="/catalog"
                            className="group inline-flex items-center justify-center gap-2 text-white font-semibold px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 8px 32px rgba(59,130,246,0.35)' }}>
                            Shop Products
                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                        </a>
                        <a href="/training-guides"
                            className="inline-flex items-center justify-center gap-2 text-slate-300 hover:text-white font-medium px-9 py-4 rounded-full border border-white/15 hover:border-white/35 transition-all duration-300"
                            style={{ backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.04)' }}>
                            Free Taping Guides
                        </a>
                    </div>

                    {/* Floating trust chips */}
                    <div className="flex flex-wrap justify-center gap-3 mt-14">
                        {['✓ Free delivery nationwide', '✓ Pay by transfer or DuitNow', '✓ Tracked shipping', '✓ SSM-registered business'].map((t) => (
                            <span key={t} className="text-xs text-slate-400 border border-white/10 rounded-full px-4 py-1.5 font-light"
                                style={{ background: 'rgba(255,255,255,0.04)' }}>
                                {t}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Curved bottom wave */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 80L60 69.3C120 58.7 240 37.3 360 32C480 26.7 600 37.3 720 42.7C840 48 960 48 1080 42.7C1200 37.3 1320 26.7 1380 21.3L1440 16V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="white" />
                    </svg>
                </div>
            </section>

            {/* ── PROMISES ──────────────────────────────────────────── */}
            <section className="bg-white pt-2 pb-16">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {PROMISES.map((p, i) => (
                            <div key={i} className="group bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 rounded-3xl py-8 px-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-100">
                                <p className="text-base font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">{p.title}</p>
                                <p className="text-sm text-slate-500 font-light leading-relaxed">{p.sub}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── DELIVERY BANNER ───────────────────────────────────── */}
            <section className="mx-6 lg:mx-auto max-w-6xl mb-16">
                <div className="rounded-3xl overflow-hidden relative"
                    style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)' }}>
                    <div className="absolute right-0 top-0 bottom-0 w-64 opacity-10"
                        style={{ background: 'radial-gradient(circle at 100% 50%, #fff 0%, transparent 70%)' }} />
                    <div className="relative px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-white text-sm font-light">
                            <span className="font-semibold">Free delivery on every order</span> — anywhere in Malaysia, no minimum spend.
                        </p>
                        <a href="/catalog" className="text-white text-sm font-semibold whitespace-nowrap bg-white/15 hover:bg-white/25 px-5 py-2 rounded-full transition-all duration-300">
                            Shop Now →
                        </a>
                    </div>
                </div>
            </section>

            {/* ── CATEGORIES ────────────────────────────────────────── */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <p className="text-xs font-bold tracking-widest text-blue-600 mb-3 uppercase">What We Offer</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900">Where to start</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {CATEGORIES.map((cat, i) => (
                            <div key={i}
                                className="group relative bg-slate-50 hover:bg-white border border-slate-100 hover:border-blue-200 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-100 overflow-hidden">
                                {/* bg circle decoration */}
                                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)' }} />
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 group-hover:from-blue-200 group-hover:to-indigo-200 flex items-center justify-center text-blue-600 mb-6 transition-all duration-300">
                                    {cat.icon}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{cat.title}</h3>
                                <p className="text-slate-500 font-light text-sm leading-relaxed mb-6">{cat.desc}</p>
                                <a href={cat.href}
                                    {...(cat.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
                                    className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                                    {cat.label}
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                    </svg>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wave into products section */}
            <div className="bg-white">
                <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
                    <path d="M0 0L80 10.7C160 21.3 320 42.7 480 48C640 53.3 800 42.7 960 37.3C1120 32 1280 32 1360 32L1440 32V60H1360C1280 60 1120 60 960 60C800 60 640 60 480 60C320 60 160 60 80 60H0V0Z" fill="#f8fafc" />
                </svg>
            </div>

            {/* ── FEATURED PRODUCTS ──────────────────────────────────── */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4">
                        <div>
                            <p className="text-xs font-bold tracking-widest text-blue-600 mb-3 uppercase">From the catalogue</p>
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">What we stock</h2>
                        </div>
                        <a href="/catalog" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-6 py-3 rounded-full transition-all duration-300 border border-blue-200">
                            View all →
                        </a>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="rounded-3xl overflow-hidden bg-white">
                                    <div className="shimmer h-56 w-full" />
                                    <div className="p-6 space-y-3">
                                        <div className="shimmer h-4 w-3/4 rounded-full" />
                                        <div className="shimmer h-3 w-full rounded-full" />
                                        <div className="shimmer h-3 w-2/3 rounded-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {products.map((product) => (
                                <ProductCard key={product._id} product={product} onAddToCart={handleAddToCart} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Wave into dark section */}
            <div className="bg-slate-50">
                <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
                    <path d="M0 60L80 49.3C160 38.7 320 17.3 480 10.7C640 4 800 10.7 960 21.3C1120 32 1280 53.3 1360 58.7L1440 64V0H1360C1280 0 1120 0 960 0C800 0 640 0 480 0C320 0 160 0 80 0H0V60Z" fill="#0f172a" />
                </svg>
            </div>

            {/* ── WHY DOCTAPE ────────────────────────────────────────── */}
            <section className="py-24 bg-slate-900">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-xs font-bold tracking-widest text-blue-400 mb-4 uppercase">Why buy from us</p>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
                                We are new,<br />
                                <span style={{
                                    background: 'linear-gradient(135deg, #60a5fa, #818cf8)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}>so we try harder</span>
                            </h2>
                            <div className="space-y-5">
                                {[
                                    { title: 'A short, honest range', desc: 'We stock the tapes and bandages we know work, in the sizes people actually reorder — not a catalogue of a thousand things.' },
                                    { title: 'Every order matters', desc: 'Orders are packed by hand, by us. If something is wrong we will hear about it directly and fix it directly.' },
                                    { title: 'Straight answers', desc: 'Ask what a tape is for and you get a straight answer, including when the cheaper option is the right one.' },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center"
                                            style={{ background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)' }}>
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-white font-semibold mb-1">{item.title}</p>
                                            <p className="text-slate-400 font-light text-sm leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { icon: <BadgeCheck className="w-6 h-6" />, title: 'Registered', sub: 'TN MEDICA ENTERPRISE, SSM' },
                                { icon: <Truck className="w-6 h-6" />, title: 'Free Delivery', sub: 'Nationwide, no minimum' },
                                { icon: <PackageSearch className="w-6 h-6" />, title: 'Tracked', sub: 'Courier and tracking number' },
                                { icon: <MessageCircle className="w-6 h-6" />, title: 'WhatsApp', sub: 'Talk to a person' },
                            ].map((b, i) => (
                                <div key={i} className="border border-white/10 hover:border-blue-500/40 rounded-3xl p-6 transition-all duration-300 hover:-translate-y-0.5"
                                    style={{ background: 'rgba(255,255,255,0.04)' }}>
                                    <div className="text-blue-400 mb-3">{b.icon}</div>
                                    <p className="text-white font-semibold text-sm mb-1">{b.title}</p>
                                    <p className="text-slate-500 text-xs font-light">{b.sub}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Wave into testimonials */}
            <div className="bg-slate-900">
                <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
                    <path d="M0 0L120 8C240 16 480 32 720 34.7C960 37.3 1200 26.7 1320 21.3L1440 16V50H1320C1200 50 960 50 720 50C480 50 240 50 120 50H0V0Z" fill="#080812" />
                </svg>
            </div>

            {/* ── HOW ORDERING WORKS ─────────────────────────────────── */}
            <section className="py-24" style={{ background: '#080812' }}>
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <p className="text-xs font-bold tracking-widest text-blue-500 mb-3 uppercase">How it works</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Ordering takes four steps</h2>
                        <p className="text-slate-400 font-light max-w-xl mx-auto leading-relaxed">
                            We handle payment over WhatsApp rather than a card gateway. Here is exactly what happens.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((s) => (
                            <div key={s.n}
                                className="group rounded-3xl p-8 border border-white/8 hover:border-blue-500/30 transition-all duration-500 hover:-translate-y-1"
                                style={{ background: 'rgba(255,255,255,0.04)' }}>
                                <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-base font-bold mb-5"
                                    style={{ background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)' }}>
                                    {s.n}
                                </div>
                                <p className="text-white font-semibold mb-2 leading-snug">{s.title}</p>
                                <p className="text-slate-400 font-light text-sm leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FAQ ────────────────────────────────────────────────── */}
            <section className="py-24 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <p className="text-xs font-bold tracking-widest text-blue-600 mb-3 uppercase">Before you order</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900">Fair questions</h2>
                    </div>
                    <div className="space-y-4">
                        {FAQS.map((f, i) => (
                            <details key={i} className="group bg-slate-50 hover:bg-blue-50/50 border border-slate-100 rounded-3xl px-7 py-6 transition-colors duration-300">
                                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none text-slate-900 font-semibold">
                                    {f.q}
                                    <svg className="w-5 h-5 flex-shrink-0 text-blue-600 transition-transform duration-300 group-open:rotate-45"
                                        fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                </summary>
                                <p className="text-slate-500 font-light text-sm leading-relaxed mt-4">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── NEWSLETTER ─────────────────────────────────────────── */}
            <section className="py-24 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 60%, #7c3aed 100%)' }}>
                {/* Decorative circles matching logo style */}
                <div className="absolute -left-20 -top-20 w-64 h-64 rounded-full border border-white/10 pointer-events-none" />
                <div className="absolute -left-10 -top-10 w-48 h-48 rounded-full border border-white/10 pointer-events-none" />
                <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full border border-white/10 pointer-events-none" />

                <div className="relative max-w-2xl mx-auto px-6 text-center">
                    <div className="inline-flex w-16 h-16 rounded-3xl bg-white/15 items-center justify-center mb-6 mx-auto">
                        <MessageCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Still deciding?</h2>
                    <p className="text-blue-100 font-light mb-10 text-lg leading-relaxed">
                        Message us on WhatsApp. Tell us what you are taping and we will tell you what you need —
                        even if that turns out to be nothing from us.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-700 font-bold rounded-2xl hover:bg-blue-50 transition-all duration-300 hover:shadow-xl whitespace-nowrap text-sm">
                            <MessageCircle className="w-4 h-4" />
                            Message us on WhatsApp
                        </a>
                        <a href="/catalog"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white font-medium rounded-2xl border border-white/25 hover:border-white/50 transition-all duration-300 whitespace-nowrap text-sm"
                            style={{ background: 'rgba(255,255,255,0.10)' }}>
                            Browse the catalogue
                        </a>
                    </div>
                    <p className="text-blue-200/70 text-xs mt-5 font-light">TN MEDICA ENTERPRISE · Replies during business hours</p>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
